import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./index.module.css";

import Dialog from "@/components/Dialog";
import DoctorProfile from "@/views/Common/DoctorProfile";
import ProfileStatusBadge from "../ProfileStatusBadge";

import { styles } from "@/utils/styles";
import {
  capitalizeFirstLetter,
  randomCardColorGenerator,
  transformClasses,
} from "@/utils";

import { IDoctorProfileStatus, IKeyPairsValues } from "@/models/doctor";
import {
  IAllOrderTypeEnums,
  IOrderStatus,
  IOrderStatusEnums,
} from "@/models/ApiRequestResponse/patient";

import PersonImage from "@/assets/p1.png";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { config, setIsShowUniversalSearch } from "@/store/slices/config";

interface IProps {
  doctorId?: string;
  image?: string;
  imageSvg?: React.ReactNode;
  orderStatus?: IOrderStatus;
  status?: IDoctorProfileStatus;
  name: string;
  bio: string;
  languages?: string;
  liveQueue?: number;
  date?: string;
  orderType?: IAllOrderTypeEnums;
  profileTags?: string[];
}

const orderStatusMap: IKeyPairsValues = {
  [IOrderStatus.COMPLETED]: style.statusCompleted,
  [IOrderStatus.CONFIRMED]: style.statusUpcoming,
  [IOrderStatus.CANCELLED]: style.statusCancelled,
  [IOrderStatus.PENDING]: style.statusUpcoming,
  [IOrderStatus.UPCOMING]: style.statusUpcoming,
  [IOrderStatus.UPLOADED]: style.statusUpcoming,
  [IOrderStatus.ADMIT]: style.statusUpcoming,
  [IOrderStatus.REJECTED]: style.statusUpcoming,
  [IOrderStatus.DISCHARGED]: style.statusDischarged,
};

const GenericCardHeader: React.FC<IProps> = (props) => {
  const {
    image,
    imageSvg,
    bio,
    name,
    languages,
    doctorId,
    orderStatus,
    liveQueue,
    date,
    orderType,
    status,
    profileTags,
  } = props;

  const [isDoctorDetailShow, setIsDoctorDetailShow] = useState<boolean>(false);

  const navigate = useNavigate();

  const today = dayjs().format("YYYY-MM-DD");

  const dispatch = useAppDispatch();
  const { isShowUniversalSearch } = useAppSelector(config);

  const isPendingOrUpcoming =
    orderStatus === IOrderStatus.PENDING ||
    orderStatus === IOrderStatus.UPCOMING;
  const isConfirmed = orderStatus === IOrderStatus.CONFIRMED;

  const isToday = date && today === dayjs(date).format("YYYY-MM-DD");

  const isHospitalAdmit =
    orderType === IAllOrderTypeEnums.HOSPITAL &&
    orderStatus === IOrderStatus.ADMIT;

  const isAppointment = orderType === IAllOrderTypeEnums.APPOINTMENT;
  const isDiagnosticTest = orderType === IAllOrderTypeEnums.DIAGNOSTIC_TEST;

  let displayStatus = capitalizeFirstLetter(orderStatus);

  const viewDoctorProfilePage = () => {
    if (!doctorId) return;
    navigate(`/doctor-profile`, { state: { doctorId } });
    if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
    }
  };

  if (isPendingOrUpcoming && liveQueue && isToday) {
    displayStatus = `Live Queue: ${liveQueue} people before you`;
  } else if (isHospitalAdmit) {
    displayStatus = IOrderStatusEnums.ACTIVE;
  } else if (isPendingOrUpcoming) {
    if (isAppointment) {
      displayStatus = capitalizeFirstLetter(IOrderStatus.UPCOMING);
    } else if (isDiagnosticTest) {
      displayStatus = IOrderStatusEnums.PENDING;
    } else {
      displayStatus = capitalizeFirstLetter(IOrderStatus.PENDING);
    }
  } else if (isConfirmed) {
    displayStatus = IOrderStatusEnums.ACCEPTED;
  }

  return (
    <>
      <Grid
        item
        className={style.headerContainer}
        onClick={viewDoctorProfilePage}
      >
        <Grid item className={style.imageContainer}>
          {imageSvg ? (
            imageSvg
          ) : (
            <img
              src={image ?? PersonImage}
              alt="doctorPhoto"
              width={56}
              height={56}
            />
          )}{" "}
          {status === IDoctorProfileStatus.VERIFIED && (
            <Grid item className={style.profileStatus}>
              <ProfileStatusBadge status={status} />
            </Grid>
          )}
        </Grid>
        <Grid item sx={styles.ml8}>
          <Typography
            component={"h6"}
            className={style.name}
            textAlign={"left"}
          >
            {name}
          </Typography>
          <Typography component={"p"} className={style.bio} textAlign={"left"}>
            {bio}
          </Typography>
          {languages && (
            <Typography
              component={"p"}
              className={style.languages}
              textAlign={"left"}
            >
              {languages}
            </Typography>
          )}
          {profileTags && (
            <Grid item className={style.profileTagsContainer}>
              {profileTags?.map((tag) => (
                <Typography
                  component={"p"}
                  className={style.profileTags}
                  textAlign={"left"}
                  sx={{ backgroundColor: randomCardColorGenerator() }}
                >
                  {tag}
                </Typography>
              ))}
            </Grid>
          )}
          {orderStatus && (
            <Typography
              component={"p"}
              className={transformClasses(
                style.status,
                orderStatusMap[orderStatus]
              )}
            >
              {displayStatus}
            </Typography>
          )}
        </Grid>
      </Grid>
      {isDoctorDetailShow && (
        <Dialog
          fullWidth
          maxWidth="sm"
          isOpen={isDoctorDetailShow}
          closeHandler={() => setIsDoctorDetailShow(!isDoctorDetailShow)}
        >
          <DoctorProfile doctorID={doctorId} />
        </Dialog>
      )}
    </>
  );
};

export default GenericCardHeader;
